import { inject, Injectable } from '@angular/core';
import { map, Observable, switchMap } from 'rxjs';
import {
  IEmailQuote,
  IEmailQuoteResponse,
  IMakeQuote,
  IMakeQuoteResponse
} from '../../models/quote/basic-quote.interface';
import { TravelersStore } from '../../../core/stores/travelers.store';
import { TripDatesStore } from '../../../pages/trip-details/when-are-you-travelling/store/trip-dates.store';
import { DestinationsStore } from '../../../pages/trip-details/where-are-you-travelling/store/destinations.store';
import { Traveler } from '../../models/traveler.interface';
import { RestApiService } from '@pc/services';
import { DateRange } from '@angular/material/datepicker';
import { ApiPaths } from '../../../app.const';
import { MakeQuoteService } from './make-quote.service';
import { DateUtils } from '@pc/date';
import { BasicQuoteService } from './basic-quote.service';
import { AppSteps, StepsManagementService } from '../steps-management.service';
import { ProcessTypeStore } from '../../../store/process-type-store/process-type-store';
import { AnalyticsService } from '../analytics.service';
import { ProcessSource } from '../../models/agent/agent-models';
import { ProductsStore } from '../../../pages/covers-page/store/products.store';
@Injectable({
  providedIn: 'root'
})
export class EmailQuoteService {
  private basicQuoteService = inject(BasicQuoteService);
  private makeQuoteService = inject(MakeQuoteService);
  private stepsManagementService = inject(StepsManagementService);
  private analytics = inject(AnalyticsService);

  private destinationsStore = inject(DestinationsStore);
  private tripDatesStore = inject(TripDatesStore);
  private travelersStore = inject(TravelersStore);
  private productsStore = inject(ProductsStore);
  private processTypeStore = inject(ProcessTypeStore);

  private dateUtils = inject(DateUtils);
  private readonly restApiService = inject(RestApiService);

  public sendBasicQuoteDetails(email: string): Observable<IEmailQuoteResponse> {
    const makeQuote = this.makeQuoteService.buildMakeQuoteBody(this.getTravelersRequest());

    const emailQuoteData: IEmailQuote = {
      EmailsForQuote: email,
      MakeQuote: makeQuote
    };

    this.analytics.pushGeneralEvent('Email Quote', { step: this.stepsManagementService.currentStep() });
    return this.restApiService.post<IEmailQuoteResponse>(ApiPaths.SaveBasicQuoteDetails, emailQuoteData);
  }

  public loadBasicQuoteDetails(quoteId: string): Observable<boolean> {
    return this.restApiService
      .post<IMakeQuoteResponse>(ApiPaths.GetBasicQuoteDetails, { BasicQuoteDetailsID: quoteId })
      .pipe(
        switchMap((makeQuote) => {
          this.populateData(makeQuote.data);
          const { selectedExcessLevelId, selectedInsuredAmountId } = makeQuote.data;
          return this.basicQuoteService
            .getBasicQuote({ selectedExcessLevelId, selectedInsuredAmountId })
            .pipe(map(() => makeQuote.data));
        }),
        switchMap((quote) => {
          this.processTypeStore.setBrokerFee(quote.brokerFee || 0);
          this.travelersStore.clearFakeDOB();
          return this.stepsManagementService.navigateToStep(this.determineStepNavigation(quote));
        })
      );
  }

  private getTravelersRequest(): Traveler[] {
    return this.travelersStore
      .allTravelers()
      .map((t) => ({ ...t, dateOfBirth: t.dateOfBirth || this.getFakeDOB(t.age) }));
  }

  private populateData(quote: IMakeQuote): void {
    const tripRange = new DateRange<Date>(new Date(quote.fromDate), new Date(quote.toDate));

    this.processTypeStore.setSource(ProcessSource.EmailQuote);
    this.destinationsStore.setPickedDestinations(quote.destinations);
    this.destinationsStore.setisDomestic(
      this.destinationsStore.pickedDestinationsData().every((des) => des.IsDomestic)
    );
    this.tripDatesStore.loadCalendarRestrictions();
    this.tripDatesStore.selectDates(tripRange);
    this.loadTravelers(quote.travelers, quote.stateId, quote.fromDate);
    this.productsStore.setSelectedProductId(quote.policyGroupId);
  }

  private loadTravelers(travelers: Traveler[], stateId: number, flightDate: string): void {
    this.travelersStore.addStateId(stateId);

    travelers.forEach((traveler: Traveler) => {
      const age = this.dateUtils.calculateYearDifference(
        new Date(traveler.dateOfBirth ?? new Date()),
        new Date(flightDate)
      );

      this.travelersStore.addTraveler({ ...traveler, age });
    });
  }

  private getFakeDOB(age: number): string {
    const dateOfBirth = new Date(this.tripDatesStore.dates()?.start || new Date());
    dateOfBirth.setFullYear(dateOfBirth.getFullYear() - age);

    return dateOfBirth.toISOString();
  }

  private determineStepNavigation(quote: IMakeQuote): AppSteps {
    const travelers = quote.travelers;

    for (const traveler of travelers) {
      for (const product of traveler.productCover) {
        if (product.coversIds.length > 0) {
          return AppSteps.TravelersDetails;
        }
      }
    }

    return AppSteps.CoverageSelection;
  }
}
